import React, { useEffect, useState } from "react";
import { message, Modal } from "antd";
import {
  addDoc, collection, query, where, onSnapshot, serverTimestamp, getDocs, updateDoc
} from "firebase/firestore";
import { db } from "../firebase";
import { getAuth } from "firebase/auth";
import { QRCodeCanvas } from "qrcode.react";
import { useNavigate } from "react-router-dom";

export default function ReferFriend() {
  const auth = getAuth();
  const user = auth.currentUser;
  const navigate = useNavigate();
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showInfo, setShowInfo] = useState(true);
  const [showQRModal, setShowQRModal] = useState(false);
  const [selectedReferral, setSelectedReferral] = useState(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [dark, setDark] = useState(() => localStorage.getItem("ws-theme") === "dark");
  const [infoLang, setInfoLang] = useState("fr");

  const toggleTheme = () => {
    setDark(d => {
      localStorage.setItem("ws-theme", !d ? "dark" : "light");
      return !d;
    });
  };

  const t = dark ? theme.dark : theme.light;

  useEffect(() => {
    if (!user) return;
    const q = query(collection(db, "referrals"), where("uid", "==", user.uid));
    const unsub = onSnapshot(q, snap => {
      setReferrals(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return unsub;
  }, [user]);

  const handleRefer = async (e) => {
    e.preventDefault();
    if (!user) return message.error("Veuillez vous connecter.");
    if (!name || !email) return message.warning("Remplissez tous les champs.");
    if (!/\S+@\S+\.\S+/.test(email)) return message.warning("Email invalide.");

    setLoading(true);
    try {
      const existing = await getDocs(query(
        collection(db, "referrals"),
        where("uid", "==", user.uid),
        where("referredEmail", "==", email)
      ));
      if (!existing.empty) { setLoading(false); return message.warning("Vous avez déjà invité cet email."); }

      const docRef = await addDoc(collection(db, "referrals"), {
        uid: user.uid,
        referredEmail: email,
        referredName: name,
        referredUid: null,
        discountUsed: false,
        membershipPurchased: false,
        referralCode: "",
        qrCodeUrl: "",
        timestamp: serverTimestamp(),
      });

      const code = docRef.id;
      const url = `${window.location.origin}/signup?ref=${code}`;
      await updateDoc(docRef, { referralCode: code, qrCodeUrl: url });

      await sendReferralEmail({ to: email, name, code, invitedBy: user.displayName || user.email || "un utilisateur WeStudy" });

      message.success("Invitation envoyée avec succès ! 🎉");
      setName("");
      setEmail("");
    } catch (err) {
      console.error(err);
      message.error("Erreur lors de l'envoi.");
    } finally {
      setLoading(false);
    }
  };

  const sendReferralEmail = async ({ to, name, code, invitedBy }) => {
    try {
      await fetch("https://hook.eu2.make.com/gy44qkh57d54rynhvaq1fc4omvb22m8p", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          to, name,
          subject: "🎁 Ton code WeStudy est prêt !",
          content: `<p>Salut <strong>${name}</strong>, <strong>${invitedBy}</strong> t'a invité à rejoindre WeStudy 🎓</p><p>Code: <strong>${code}</strong></p>`,
        }),
      });
    } catch (err) { console.error("Erreur envoi email:", err); }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    message.success("Lien copié ! 📋");
  };

  const completedReferrals = referrals.filter(r => r.membershipPurchased).length;
  const pendingReferrals = referrals.filter(r => !r.membershipPurchased).length;
  const successRate = referrals.length > 0 ? Math.round((completedReferrals / referrals.length) * 100) : 0;

  const steps = {
    fr: [
      { icon: "✉️", title: "Invitez vos amis", desc: "Entrez le nom et l'email de votre ami." },
      { icon: "⏱️", title: "10 jours pour s'inscrire", desc: "Votre ami a 10 jours pour utiliser le lien." },
      { icon: "🛒", title: "5 jours pour s'abonner", desc: "Après inscription, 5 jours pour acheter un abonnement." },
      { icon: "🎉", title: "Récompenses", desc: "Parrain et parrainé reçoivent 5% de réduction." },
    ],
    en: [
      { icon: "✉️", title: "Invite your friends", desc: "Enter your friend's name and email." },
      { icon: "⏱️", title: "10 days to sign up", desc: "Your friend has 10 days to use the link." },
      { icon: "🛒", title: "5 days to subscribe", desc: "After signing up, 5 days to buy a membership." },
      { icon: "🎉", title: "Rewards", desc: "Both get 5% off their membership." },
    ],
  };

  return (
    <div style={{ background: t.bg, minHeight: "100vh" }}>
      <div style={{ maxWidth: 900, margin: "0 auto", paddingBottom: 40 }}>

        {/* Topbar */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 18px 0", flexWrap: "wrap", gap: 10 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <img src="/logo.PNG" alt="WeStudy" style={{ height: 40, objectFit: "contain" }} />
            <div>
              <div style={{ fontSize: 15, fontWeight: 600, color: t.text1 }}>Parrainage</div>
              <div style={{ fontSize: 11, color: t.text2 }}>Invite & gagne des réductions</div>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <button onClick={toggleTheme} style={{ ...s.toggleTrack, background: t.bg3, border: `1px solid ${t.border}` }}>
              <div style={{ ...s.toggleKnob, background: t.blue, transform: dark ? "translateX(16px)" : "translateX(0)" }} />
            </button>
            <button onClick={() => setShowInfo(true)} style={{ ...s.outlineBtn, background: t.bg3, border: `1px solid ${t.border}`, color: t.text2 }}>
              ℹ️ Comment ça marche
            </button>
            <button onClick={() => navigate("/")} style={{ ...s.outlineBtn, background: t.bg3, border: `1px solid ${t.border}`, color: t.text2 }}>← Retour</button>
          </div>
        </div>

        {/* Stats row */}
        {referrals.length > 0 && (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0,1fr))", gap: 10, padding: "20px 18px 0" }}>
            <StatCard icon="🔗" label="Invitations" value={referrals.length} t={t} />
            <StatCard icon="🏆" label="Validées" value={completedReferrals} t={t} color={t.green} />
            <StatCard icon="📊" label="Taux réussite" value={`${successRate}%`} t={t} color={t.blue} progress={successRate} />
          </div>
        )}

        {/* Main grid */}
        <ResponsiveGrid>
          {/* Form card */}
          <div style={{ background: t.cardBg, border: `1px solid ${t.border}`, borderRadius: 14, padding: 18 }}>
            <div style={{ fontSize: 11, fontWeight: 500, color: t.text3, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 16 }}>
              Inviter un ami
            </div>

            <form onSubmit={handleRefer} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <div>
                <label style={{ fontSize: 12, color: t.text2, display: "block", marginBottom: 5 }}>👤 Nom de l'ami</label>
                <input
                  value={name} onChange={e => setName(e.target.value)}
                  placeholder="Ex: Ahmed Ben Ali"
                  style={{ ...s.input, background: t.bg3, border: `1px solid ${t.border}`, color: t.text1 }}
                />
              </div>
              <div>
                <label style={{ fontSize: 12, color: t.text2, display: "block", marginBottom: 5 }}>✉️ Email de l'ami</label>
                <input
                  type="email" value={email} onChange={e => setEmail(e.target.value)}
                  placeholder="ami@example.com"
                  style={{ ...s.input, background: t.bg3, border: `1px solid ${t.border}`, color: t.text1 }}
                />
              </div>
              <button
                type="submit" disabled={loading}
                style={{ ...s.primaryBtn, background: loading ? t.border : t.blue, color: loading ? t.text3 : "#fff", marginTop: 4 }}
              >
                {loading ? "Envoi en cours..." : "📨 Envoyer l'invitation"}
              </button>
            </form>

            <div style={{ marginTop: 14, padding: "10px 12px", background: t.blueBg, borderRadius: 10, fontSize: 12, color: t.blueText }}>
              ℹ️ L'invitation est envoyée par email avec un QR code unique.
            </div>
          </div>

          {/* Referrals list */}
          <div style={{ background: t.cardBg, border: `1px solid ${t.border}`, borderRadius: 14, padding: 18 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
              <div style={{ fontSize: 11, fontWeight: 500, color: t.text3, letterSpacing: "0.08em", textTransform: "uppercase" }}>Mes invitations</div>
              <span style={{ fontSize: 11, background: t.bg3, color: t.text2, borderRadius: 20, padding: "1px 8px" }}>{referrals.length}</span>
            </div>

            {referrals.length === 0 ? (
              <div style={{ textAlign: "center", padding: "40px 0" }}>
                <div style={{ fontSize: 32, marginBottom: 8 }}>🎁</div>
                <div style={{ fontSize: 13, color: t.text1, marginBottom: 4 }}>Aucune invitation envoyée</div>
                <div style={{ fontSize: 12, color: t.text2 }}>Invitez vos amis pour gagner des réductions !</div>
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {referrals.map(r => (
                  <div key={r.id} style={{ background: t.bg3, border: `1px solid ${t.border}`, borderRadius: 10, padding: "12px 14px", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 500, color: t.text1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.referredName}</div>
                      <div style={{ fontSize: 11, color: t.text2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.referredEmail}</div>
                      <div style={{ display: "flex", gap: 6, marginTop: 5, flexWrap: "wrap" }}>
                        <span style={{ fontSize: 10, padding: "2px 7px", borderRadius: 20, background: r.referredUid ? t.greenBg : t.bg3, color: r.referredUid ? t.green : t.text3 }}>
                          {r.referredUid ? "✓ Inscrit" : "⏳ En attente"}
                        </span>
                        {r.membershipPurchased && (
                          <span style={{ fontSize: 10, padding: "2px 7px", borderRadius: 20, background: t.amberBg, color: t.amberText }}>🏆 Abonné</span>
                        )}
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                      <button
                        onClick={() => { setSelectedReferral(r); setShowQRModal(true); }}
                        style={{ ...s.iconBtn, background: t.blueBg, color: t.blueText, border: `1px solid ${t.border}` }}
                      >QR</button>
                      {r.qrCodeUrl && (
                        <button
                          onClick={() => copyToClipboard(r.qrCodeUrl)}
                          style={{ ...s.iconBtn, background: t.bg3, color: t.text2, border: `1px solid ${t.border}` }}
                        >📋</button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </ResponsiveGrid>

        {/* Disclaimer */}
        <div style={{ margin: "16px 18px 0", padding: "12px 14px", background: t.amberBg, border: `1px solid ${t.amberBorder}`, borderRadius: 12, fontSize: 12, color: t.amberText }}>
          ⚠️ WeStudy se réserve le droit d'arrêter la vente des abonnements, de refuser ou de révoquer un abonnement sans justification.
        </div>
      </div>

      {/* Info Modal */}
      <Modal
        open={showInfo}
        onCancel={() => setShowInfo(false)}
        footer={null}
        centered
        width={480}
        title={
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <span>🎁</span>
            <span style={{ fontSize: 15, fontWeight: 600 }}>Comment fonctionne le parrainage ?</span>
          </div>
        }
      >
        <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
          {["fr", "en"].map(lang => (
            <button
              key={lang}
              onClick={() => setInfoLang(lang)}
              style={{ flex: 1, padding: "6px", borderRadius: 8, border: `1px solid ${infoLang === lang ? "#2563eb" : "#e2e8f0"}`, background: infoLang === lang ? "#eff6ff" : "#f8fafc", color: infoLang === lang ? "#1d4ed8" : "#64748b", cursor: "pointer", fontSize: 13, fontWeight: infoLang === lang ? 600 : 400 }}
            >
              {lang === "fr" ? "🇫🇷 Français" : "🇺🇸 English"}
            </button>
          ))}
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {steps[infoLang].map((step, i) => (
            <div key={i} style={{ display: "flex", gap: 12, padding: "12px 14px", background: "#f8fafc", borderRadius: 10, alignItems: "flex-start" }}>
              <div style={{ width: 36, height: 36, borderRadius: 10, background: "#eff6ff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, flexShrink: 0 }}>{step.icon}</div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: "#0f172a", marginBottom: 2 }}>{step.title}</div>
                <div style={{ fontSize: 12, color: "#64748b" }}>{step.desc}</div>
              </div>
            </div>
          ))}
        </div>
        <div style={{ marginTop: 14, padding: "10px 14px", background: "#eff6ff", borderRadius: 10, fontSize: 12, color: "#1d4ed8" }}>
          📌 {infoLang === "fr" ? "Vous pouvez inviter autant d'amis que vous souhaitez !" : "You can send unlimited referral links!"}
        </div>
      </Modal>

      {/* QR Modal */}
      <Modal
        open={showQRModal}
        onCancel={() => setShowQRModal(false)}
        footer={null}
        centered
        width={340}
        title="QR Code d'invitation"
      >
        {selectedReferral && (
          <div style={{ textAlign: "center", paddingTop: 8 }}>
            <div style={{ display: "inline-block", padding: 16, background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", marginBottom: 14 }}>
              <QRCodeCanvas value={selectedReferral.qrCodeUrl || selectedReferral.referralCode} size={180} />
            </div>
            <div style={{ fontSize: 14, fontWeight: 600, color: "#0f172a" }}>{selectedReferral.referredName}</div>
            <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>{selectedReferral.referredEmail}</div>
            <div style={{ fontSize: 11, padding: "3px 10px", borderRadius: 20, background: "#eff6ff", color: "#1d4ed8", display: "inline-block", marginTop: 8 }}>
              Code: {selectedReferral.referralCode}
            </div>
            {selectedReferral.qrCodeUrl && (
              <button
                onClick={() => copyToClipboard(selectedReferral.qrCodeUrl)}
                style={{ display: "block", width: "100%", marginTop: 14, padding: "9px", borderRadius: 10, border: "1px solid #e2e8f0", background: "#f8fafc", color: "#2563eb", fontSize: 13, fontWeight: 500, cursor: "pointer" }}
              >
                📋 Copier le lien
              </button>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}

function StatCard({ icon, label, value, t, color, progress }) {
  return (
    <div style={{ background: t.cardBg, border: `1px solid ${t.border}`, borderRadius: 14, padding: 14 }}>
      <div style={{ fontSize: 18, marginBottom: 6 }}>{icon}</div>
      <div style={{ fontSize: 20, fontWeight: 600, color: color || t.text1 }}>{value}</div>
      <div style={{ fontSize: 11, color: t.text2, marginTop: 2 }}>{label}</div>
      {progress !== undefined && (
        <div style={{ height: 3, borderRadius: 3, background: t.bg3, marginTop: 8, overflow: "hidden" }}>
          <div style={{ height: "100%", borderRadius: 3, background: color || t.blue, width: `${progress}%` }} />
        </div>
      )}
    </div>
  );
}

function ResponsiveGrid({ children }) {
  const [cols, setCols] = useState(1);
  useEffect(() => {
    const update = () => setCols(window.innerWidth >= 768 ? 2 : 1);
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);
  return (
    <div style={{ display: "grid", gridTemplateColumns: `repeat(${cols}, minmax(0,1fr))`, gap: 10, padding: "16px 18px 0" }}>
      {children}
    </div>
  );
}

const theme = {
  light: {
    bg: "#f8fafc", bg3: "#f1f5f9", border: "#e2e8f0",
    text1: "#0f172a", text2: "#64748b", text3: "#94a3b8",
    blue: "#2563eb", blueBg: "#eff6ff", blueText: "#1d4ed8",
    green: "#16a34a", greenBg: "#f0fdf4",
    amber: "#d97706", amberBg: "#fffbeb", amberText: "#b45309", amberBorder: "#fde68a",
    cardBg: "#ffffff",
  },
  dark: {
    bg: "#0f172a", bg3: "#1a2740", border: "#273549",
    text1: "#f1f5f9", text2: "#94a3b8", text3: "#475569",
    blue: "#378ADD", blueBg: "#1e3a5f", blueText: "#85B7EB",
    green: "#4ade80", greenBg: "#052e16",
    amber: "#fbbf24", amberBg: "#2a1e07", amberText: "#fcd34d", amberBorder: "#78350f",
    cardBg: "#1e293b",
  },
};

const s = {
  toggleTrack: { width: 36, height: 20, borderRadius: 20, cursor: "pointer", position: "relative", flexShrink: 0 },
  toggleKnob: { width: 14, height: 14, borderRadius: "50%", position: "absolute", top: 2, left: 2, transition: "transform 0.2s" },
  outlineBtn: { fontSize: 12, borderRadius: 8, padding: "6px 10px", cursor: "pointer", whiteSpace: "nowrap" },
  primaryBtn: { width: "100%", height: 42, borderRadius: 10, border: "none", fontSize: 13, fontWeight: 600, cursor: "pointer", transition: "opacity 0.15s" },
  input: { width: "100%", height: 40, borderRadius: 10, padding: "0 12px", fontSize: 13, outline: "none", boxSizing: "border-box" },
  iconBtn: { fontSize: 11, fontWeight: 600, padding: "5px 10px", borderRadius: 8, cursor: "pointer" },
};